import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import type {
  ReviewStockClipBody,
  StockClipTagStatus,
  StockClipsForReview,
} from '@borradh-workspace/api-client/types';
import {
  reviewStockClipResponseSchema,
  stockClipsForReviewResponseSchema,
} from '@borradh-workspace/contracts';
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

export const STOCK_REVIEW_PAGE_SIZE = 20;

export interface StockReviewQuery {
  eligibleForServiceId?: string;
  /** One status, or `live`: every clip in use (set by AI or checked). */
  status: StockClipTagStatus | 'live';
  offset: number;
  /** Page size; defaults to STOCK_REVIEW_PAGE_SIZE. */
  limit?: number;
  /** Only clips of this treatment. */
  techniqueSlug?: string;
  /** Only generic clips (no treatment shown). */
  generic?: boolean;
  /** Words to find in the description or stock title. */
  search?: string;
}

interface ReviewStockClipResult {
  id: string;
  tagStatus: StockClipTagStatus;
  servicesRematched: number;
}

export const stockClipsForReviewQueryOptions = (params: StockReviewQuery) =>
  queryOptions({
    queryKey: queryKeys.adminTerminal.stockFootageClips(params),
    queryFn: () =>
      apiClient.get<StockClipsForReview>('admin-terminal/stock-footage/clips', {
        schema: stockClipsForReviewResponseSchema,
        searchParams: {
          ...(params.eligibleForServiceId
            ? { eligibleForServiceId: params.eligibleForServiceId }
            : {}),
          status: params.status,
          limit: params.limit ?? STOCK_REVIEW_PAGE_SIZE,
          offset: params.offset,
          ...(params.techniqueSlug
            ? { techniqueSlug: params.techniqueSlug }
            : {}),
          ...(params.generic ? { generic: 'true' } : {}),
          ...(params.search ? { search: params.search } : {}),
        },
      }),
    placeholderData: keepPreviousData,
    // Previews are signed links that expire; a page left open for a long time
    // refetches rather than showing dead players.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

/** One page of the stock clip review queue (admin). */
export const useStockClipsForReview = (params: StockReviewQuery) => {
  const query = useQuery(stockClipsForReviewQueryOptions(params));
  return {
    page: query.data ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};

/**
 * A person's decision on one stock clip. Confirming makes it matchable and
 * re-matches every service with that treatment; rejecting takes it out of the
 * bank. `onDone` runs once the decision is saved, so the review queue can move
 * to the next clip without waiting for the list to refetch.
 */
export const useReviewStockClip = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: { stockClipId: string; body: ReviewStockClipBody }) =>
      apiClient.post<ReviewStockClipResult>(
        `admin-terminal/stock-footage/clips/${encodeURIComponent(input.stockClipId)}/review`,
        input.body,
        { schema: reviewStockClipResponseSchema }
      ),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.adminTerminal.stockFootage(),
      });
      if (result.tagStatus === 'confirmed') {
        toast.success(
          result.servicesRematched > 0
            ? `Confirmed. ${result.servicesRematched} ${result.servicesRematched === 1 ? 'service is' : 'services are'} picking it up.`
            : 'Confirmed.'
        );
      } else {
        toast.success('Rejected. It will not be used.');
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Could not save the review');
    },
  });

  return {
    review: (
      stockClipId: string,
      body: ReviewStockClipBody,
      onDone?: () => void
    ) =>
      mutation.mutate(
        { stockClipId, body },
        onDone ? { onSuccess: () => onDone() } : undefined
      ),
    isReviewing: mutation.isPending,
    reviewingId: mutation.isPending
      ? (mutation.variables?.stockClipId ?? null)
      : null,
  };
};
